/* eslint-disable @typescript-eslint/no-explicit-any */
import { createStore } from '@oneappexchange/lwc-wired';
import { UnleashClient } from 'unleash-proxy-client';
import { FEATURE_FLAG_APP_NAME, FEATURE_FLAG_CLIENT_KEY, FEATURE_FLAG_PROXY_URL, FEATURE_FLAG_REFRESH_INTERVAL, IS_DEVELOPMENT } from '~/config';
import { handleNetworkWarning } from '../utilities/axiosUtilities';
import { deepEquals } from '../utilities/objectUtilities';
export let FeatureFlagStoreStatus = /*#__PURE__*/function (FeatureFlagStoreStatus) {
  FeatureFlagStoreStatus["UNINITIALIZED"] = "UNINITIALIZED";
  FeatureFlagStoreStatus["LOADING"] = "LOADING";
  FeatureFlagStoreStatus["LOADED"] = "LOADED";
  FeatureFlagStoreStatus["ERROR"] = "ERROR";
  return FeatureFlagStoreStatus;
}({});
const initialState = {
  status: FeatureFlagStoreStatus.UNINITIALIZED,
  featureFlags: Object.create(null)
};
export const featureFlagsStore = createStore(initialState, (set, get) => ({
  // This CAN ONLY be invoked from the user store, after user data is acquired.
  // Because fetching feature flags depends on the user data for context.
  getFeatureFlags({
    context,
    defaultValues
  } = {}) {
    if (!FEATURE_FLAG_PROXY_URL) {
      throw new Error('Cannot fetch feature flags without "FEATURE_FLAG_PROXY_URL" env var');
    }
    if (!FEATURE_FLAG_CLIENT_KEY) {
      throw new Error('Cannot fetch feature flags without "FEATURE_FLAG_CLIENT_KEY" env var');
    }
    if (!FEATURE_FLAG_APP_NAME) {
      throw new Error('Cannot fetch feature flags without "FEATURE_FLAG_APP_NAME" env var');
    }
    let unleash = get().unleash;

    // If we already created unleash client, and the context did not change, skip re-fetching FFs.
    if (unleash && deepEquals(get().context, context)) {
      // If current featureFlags differ from defaultValues, update them to the new defaultValues.
      if (defaultValues && !deepEquals(get().featureFlags, defaultValues)) {
        set(function setFeatureFlags(draft) {
          draft.featureFlags = defaultValues;
        });
      }
      // Skip re-fetching FFs.
      return Promise.resolve();
    }

    // Create a promise that will resolve when featureFlagsStore's status field is LOADED or ERROR.
    // This promise will provide consumers an easy way to wait for FFs to be loaded.
    let resolvePromise;
    let rejectPromise;
    const statusPromise = new Promise((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });
    let unsubscribe = featureFlagsStore.subscribe(status => {
      switch (status) {
        case FeatureFlagStoreStatus.LOADED:
          {
            var _unsubscribe, _resolvePromise;
            (_unsubscribe = unsubscribe) === null || _unsubscribe === void 0 ? void 0 : _unsubscribe();
            unsubscribe = undefined;
            (_resolvePromise = resolvePromise) === null || _resolvePromise === void 0 ? void 0 : _resolvePromise();
            resolvePromise = undefined;
            break;
          }
        case FeatureFlagStoreStatus.ERROR:
          {
            var _unsubscribe2, _rejectPromise;
            (_unsubscribe2 = unsubscribe) === null || _unsubscribe2 === void 0 ? void 0 : _unsubscribe2();
            unsubscribe = undefined;
            (_rejectPromise = rejectPromise) === null || _rejectPromise === void 0 ? void 0 : _rejectPromise();
            rejectPromise = undefined;
            break;
          }
        default:
          {
            /* noop */
          }
      }
    }, selectStatus);
    try {
      if (unleash) {
        unleash.stop();
      }
      unleash = new UnleashClient({
        url: `${FEATURE_FLAG_PROXY_URL}/proxy`,
        clientKey: FEATURE_FLAG_CLIENT_KEY,
        appName: FEATURE_FLAG_APP_NAME,
        refreshInterval: Number(FEATURE_FLAG_REFRESH_INTERVAL)
      });

      // Order matters here
      set(function beginGetFeatureFlags(draft) {
        draft.unleash = unleash;
        draft.context = context;
        draft.status = FeatureFlagStoreStatus.LOADING;
        if (defaultValues) {
          draft.featureFlags = defaultValues;
        }
      });
      if (context) {
        unleash.updateContext(context);
      }

      // Listen to update because it will also be emitted on initialization,
      // we don't need to handle ready or initialized.
      unleash.on('update', () => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const featureFlags = unleash.getAllToggles().reduce((accumulator, current) => {
            accumulator[current.name] = current.enabled;
            return accumulator;
          }, {});
          set(function endGetFeatureFlags(draft) {
            draft.featureFlags = featureFlags;
            draft.status = FeatureFlagStoreStatus.LOADED;
          });
        } catch {
          set(function getFeatureFlagsFailed(draft) {
            draft.status = FeatureFlagStoreStatus.ERROR;
            console.warn(`Unable to retrieve updated feature toggles`, 'featureFlagStore');
          });
        }
      });
      unleash.on('error', () => {
        set(function getFeatureFlagsFailed(draft) {
          draft.status = FeatureFlagStoreStatus.ERROR;
        });
      });

      // We have to setup all the promise and event handlers before calling start.
      // This should be the last thing we do in this function, other than waiting the promise.
      unleash.start();
    } catch (error) {
      handleNetworkWarning(error);
      console.error('Encountered error starting feature flag service.', 'featureFlagStore');
      set(function getFeatureFlagsFailed(draft) {
        draft.status = FeatureFlagStoreStatus.ERROR;
      });
    }
    return statusPromise;
  },
  reset() {
    set(function reset() {
      return initialState;
    });
  }
}), 'FEATURE_FLAGS', IS_DEVELOPMENT);
export function selectStatus(state) {
  return state.status;
}
export function selectFeatureFlags(state) {
  return state.featureFlags;
}
export function selectFeatureFlagsLoaded(state) {
  return state.status === FeatureFlagStoreStatus.LOADED || state.status === FeatureFlagStoreStatus.ERROR;
}